/**
 * Platform Admin — Integrations dashboard (S12 + Composio C9).
 *
 * Operational read-only view of every PLATFORM-tier integration in
 * the catalog: per-integration health probe (Composio toolkit
 * reachability + latency), 24h rolling call count, 24h rolling
 * error rate, and an "Open in Composio" deep-link to the Composio
 * dashboard for credential rotation / auth-config inspection. We
 * never store secrets locally — see ADR-0005 (Composio replaces
 * Nango as the OAuth broker; supersedes ADR-0001).
 *
 * Auth posture: the server rejects non-`platformAdmin` callers with
 * FORBIDDEN (see `requirePlatformAdmin`); the page surfaces that
 * rejection as a top-level alert rather than a redirect. The route
 * is intentionally unlinked from the main nav so casual users don't
 * stumble on it; the URL is shared out-of-band with platform ops.
 *
 * Probe column (Composio C9): the per-row dot + detail are sourced
 * from the connector's `healthCheck()` probe via the existing
 * `platformIntegrations` query; the query was extended in C9 with
 * `healthMessage`, `latencyMs`, and `composioToolkitSlug` so
 * operators can read the Composio-side detail (e.g. "Composio
 * toolkit gmail reachable", "no toolkit (code-path-only)") from
 * the table without tailing logs. The "is this code-path-only?"
 * decision uses the null-slug signal from the GraphQL payload —
 * `healthMessage` is human detail only and is never switched on
 * by the UI. Refreshing the page (or the Refresh button) re-runs
 * every connector's `healthCheck()`; there is no auto-poll by
 * design.
 *
 * The "Status" column shows the existing `HEALTHY` / `UNHEALTHY`
 * / `UNKNOWN` chip from S12; the "Probe" column shows the dot +
 * detail introduced by C9. The two columns are deliberately
 * separate so operators can scan the chip-coloured Status at a
 * glance and dive into the Probe column for the underlying
 * Composio diagnostic when needed.
 */

import React from 'react';
import {
  Container,
  Box,
  Typography,
  Paper,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Button,
  CircularProgress,
  Link,
  Tooltip,
} from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import RefreshIcon from '@mui/icons-material/Refresh';
import {useQuery} from '@apollo/client';
import {useDocumentTitle} from '../hooks/useDocumentTitle';
import {GET_PLATFORM_INTEGRATIONS} from '../graphql/queries';
import {IntegrationLogo} from '../integrations';

interface PlatformIntegrationStatusRow {
  integrationId: string;
  health: 'HEALTHY' | 'UNHEALTHY' | 'UNKNOWN' | string;
  lastHealthCheckAt: string;
  rollingCallCount24h: number;
  rollingErrorRate24h: number;
  healthMessage?: string | null;
  latencyMs?: number | null;
  composioToolkitSlug?: string | null;
}

interface PlatformIntegrationsQueryResult {
  platformIntegrations: PlatformIntegrationStatusRow[];
}

const COMPOSIO_DASHBOARD_BASE = 'https://app.composio.dev';

function composioRotateUrl(slug: string): string {
  // Deep-link to the toolkit's detail page in the Composio dashboard.
  // The dashboard organises auth configs by toolkit slug —
  // `https://app.composio.dev/toolkits/<slug>` opens the toolkit
  // detail page where the Auth Configs panel lives, which is the
  // operator's entry point for rotating credentials, swapping
  // managed→custom OAuth apps, or inspecting the active `ac_…` id.
  //
  // IMPORTANT: callers pass the Composio toolkit slug, NOT the
  // Planapus integration id. The two are not always equal — e.g.
  // `google-drive` → `googledrive`, `microsoft-teams` →
  // `microsoft_teams`, `google-mail` → `gmail`,
  // `google-calendar` → `googlecalendar`. The resolver surfaces the
  // authoritative slug on `PlatformIntegrationStatus.composioToolkitSlug`
  // (sourced from `integrations.yaml`); when that slug is null
  // (SharePoint-style catalog gap) the row is code-path-only and
  // the caller should hide the link rather than building it.
  //
  // TODO(ops): if Composio publishes a stable per-auth-config deep
  // link (e.g. `/auth-configs/<ac_id>`), prefer that — it skips the
  // toolkit-detail click and lands directly on the rotate UI. For
  // now `/toolkits/<slug>` is the lowest-assumption path.
  return `${COMPOSIO_DASHBOARD_BASE}/toolkits/${encodeURIComponent(slug)}`;
}

/**
 * "Is this row code-path-only?" — true when the Composio catalog
 * doesn't carry a toolkit for this integration (SharePoint-style
 * gap). The typed signal is a null `composioToolkitSlug` from the
 * GraphQL payload, NOT a regex on `healthMessage`. This keeps the
 * UI's bucket decision robust to a future reword of the connector's
 * probe message; `healthMessage` is human detail only.
 */
function isCodePathOnly(row: PlatformIntegrationStatusRow): boolean {
  return row.composioToolkitSlug == null;
}

function healthChipColor(
  h: PlatformIntegrationStatusRow['health'],
): 'success' | 'error' | 'warning' | 'default' {
  switch (h) {
    case 'HEALTHY':
      return 'success';
    case 'UNHEALTHY':
      return 'error';
    case 'UNKNOWN':
      return 'warning';
    default:
      return 'default';
  }
}

/**
 * Map the row's health probe shape into the visual dot the operator
 * sees in the Probe column. The buckets mirror the three branches
 * the Composio platform connector's `healthCheck()` can return:
 *
 *   - reachable Composio toolkit (`healthy && composioToolkitSlug`)
 *     → success.main dot + latency display.
 *   - catalog-gap / code-path-only connector
 *     (`healthy && composioToolkitSlug == null`) → grey dot + label.
 *   - everything else (`!healthy`) → error.main dot + truncated
 *     message in a tooltip.
 *
 * UNKNOWN ("not configured") collapses into the grey bucket — it
 * isn't a failure, just an environment where the probe couldn't run
 * (e.g. local dev without COMPOSIO_API_KEY).
 *
 * The code-path-only decision uses `composioToolkitSlug == null` —
 * the typed signal from the GraphQL payload — NOT a substring match
 * on `healthMessage`. The message is human detail only.
 */
function healthDotColor(
  row: PlatformIntegrationStatusRow,
): 'success.main' | 'error.main' | 'grey.500' {
  if (row.health === 'UNHEALTHY') return 'error.main';
  if (row.health === 'UNKNOWN') return 'grey.500';
  // HEALTHY: a null slug means code-path-only (Composio catalog gap).
  // Grey dot rather than green — operators shouldn't read "reachable"
  // for an integration that has no Composio backing.
  if (isCodePathOnly(row)) return 'grey.500';
  return 'success.main';
}

function healthDetailText(row: PlatformIntegrationStatusRow): string {
  if (row.health === 'UNHEALTHY') {
    return row.healthMessage ?? 'unhealthy';
  }
  if (row.health === 'UNKNOWN') {
    return row.healthMessage ?? 'not probed';
  }
  if (isCodePathOnly(row)) {
    return 'no toolkit (code-path-only)';
  }
  if (typeof row.latencyMs === 'number') {
    return row.latencyMs > 0 ? `reachable · ${row.latencyMs}ms` : 'reachable';
  }
  return 'reachable';
}

function formatPercent(rate: number): string {
  if (!Number.isFinite(rate) || rate <= 0) return '0%';
  return `${(rate * 100).toFixed(1)}%`;
}

function formatRelative(iso: string): string {
  const t = Date.parse(iso);
  if (!Number.isFinite(t)) return iso;
  const diffMs = Date.now() - t;
  if (diffMs < 60_000) return 'just now';
  if (diffMs < 60 * 60_000) return `${Math.round(diffMs / 60_000)}m ago`;
  if (diffMs < 24 * 60 * 60_000)
    return `${Math.round(diffMs / (60 * 60_000))}h ago`;
  return new Date(t).toLocaleDateString();
}

export default function PlatformAdminIntegrationsPage() {
  useDocumentTitle('Platform Admin · Integrations');

  const {data, loading, error, refetch} =
    useQuery<PlatformIntegrationsQueryResult>(GET_PLATFORM_INTEGRATIONS, {
      fetchPolicy: 'cache-and-network',
    });

  const rows = data?.platformIntegrations ?? [];
  const isForbidden = (() => {
    if (!error) return false;
    const codes = error.graphQLErrors.map((e) => e.extensions?.code);
    return codes.includes('FORBIDDEN');
  })();

  return (
    <Box sx={{flex: 1, bgcolor: 'background.default'}}>
      <Container maxWidth="lg" sx={{py: 4}}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            mb: 1,
          }}
        >
          <Box>
            <Typography variant="h5" sx={{fontWeight: 600}}>
              Platform Admin · Integrations
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{mt: 0.5}}>
              Health, rolling 24h call volume, and credential-rotation
              entry points for every PLATFORM-tier connector. Read-only
              — credential changes are made in the Composio dashboard.
            </Typography>
          </Box>
          <Button
            startIcon={<RefreshIcon />}
            variant="outlined"
            size="small"
            onClick={() => {
              void refetch();
            }}
            disabled={loading}
          >
            Refresh
          </Button>
        </Box>

        {isForbidden && (
          <Alert severity="error" sx={{mt: 2}}>
            You don't have platform-admin permissions. This page is
            restricted to operators with the <code>platformAdmin</code>{' '}
            custom claim or an entry in{' '}
            <code>PLANAPUS_PLATFORM_ADMIN_USER_IDS</code>.
          </Alert>
        )}

        {error && !isForbidden && (
          <Alert severity="error" sx={{mt: 2}}>
            Failed to load platform integrations: {error.message}
          </Alert>
        )}

        <Paper variant="outlined" sx={{mt: 3, overflow: 'hidden'}}>
          {loading && rows.length === 0 ? (
            <Box sx={{display: 'flex', justifyContent: 'center', p: 4}}>
              <CircularProgress />
            </Box>
          ) : rows.length === 0 && !isForbidden ? (
            <Box sx={{p: 4}}>
              <Typography variant="body2" color="text.secondary">
                No PLATFORM-tier integrations are registered yet.
              </Typography>
            </Box>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{fontWeight: 600}}>Integration</TableCell>
                    <TableCell sx={{fontWeight: 600}}>Status</TableCell>
                    {/*
                      Review fix: "Probe" (probe detail) reads as the
                      diagnostic-detail column without conflicting with
                      the chip column's "Status" label. Operators
                      previously saw "Status" + "Health" side-by-side
                      and read them as contradictory.
                    */}
                    <TableCell sx={{fontWeight: 600}}>Probe</TableCell>
                    <TableCell sx={{fontWeight: 600}} align="right">
                      Calls (24h)
                    </TableCell>
                    <TableCell sx={{fontWeight: 600}} align="right">
                      Error rate (24h)
                    </TableCell>
                    <TableCell sx={{fontWeight: 600}}>Last probe</TableCell>
                    <TableCell sx={{fontWeight: 600}} align="right">
                      Credentials
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map((row) => {
                    const errPct = row.rollingErrorRate24h;
                    const isErrorElevated = errPct >= 0.1; // 10%
                    const dotColor = healthDotColor(row);
                    const detail = healthDetailText(row);
                    return (
                      <TableRow key={row.integrationId} hover>
                        <TableCell>
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1.25,
                            }}
                          >
                            <IntegrationLogo
                              integrationId={row.integrationId}
                              name={row.integrationId}
                              size={20}
                            />
                            <Typography
                              variant="body2"
                              sx={{fontFamily: 'monospace'}}
                            >
                              {row.integrationId}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            label={row.health}
                            color={healthChipColor(row.health)}
                            variant={
                              row.health === 'UNKNOWN' ? 'outlined' : 'filled'
                            }
                          />
                        </TableCell>
                        <TableCell>
                          <Tooltip
                            title={row.healthMessage ?? ''}
                            arrow
                            placement="top"
                            disableHoverListener={!row.healthMessage}
                          >
                            <Box
                              sx={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 0.75,
                                maxWidth: 240,
                              }}
                            >
                              <Box
                                component="span"
                                sx={{
                                  width: 8,
                                  height: 8,
                                  borderRadius: '50%',
                                  bgcolor: dotColor,
                                  flexShrink: 0,
                                }}
                              />
                              <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                }}
                              >
                                {detail}
                              </Typography>
                            </Box>
                          </Tooltip>
                        </TableCell>
                        <TableCell align="right">
                          {row.rollingCallCount24h.toLocaleString()}
                        </TableCell>
                        <TableCell align="right">
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: isErrorElevated ? 600 : 400,
                              color: isErrorElevated
                                ? 'error.main'
                                : 'text.primary',
                            }}
                          >
                            {formatPercent(errPct)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary">
                            {formatRelative(row.lastHealthCheckAt)}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          {row.composioToolkitSlug ? (
                            <Link
                              href={composioRotateUrl(row.composioToolkitSlug)}
                              target="_blank"
                              rel="noopener noreferrer"
                              underline="hover"
                              sx={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 0.5,
                                fontSize: '0.875rem',
                              }}
                            >
                              Open in Composio
                              <OpenInNewIcon fontSize="inherit" />
                            </Link>
                          ) : (
                            // Review fix: code-path-only rows have no
                            // Composio toolkit, so a deep-link would
                            // 404. Render plain text instead — the
                            // Probe column already explains the gap.
                            <Typography
                              variant="body2"
                              color="text.disabled"
                              sx={{fontSize: '0.875rem'}}
                            >
                              —
                            </Typography>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>

        <Typography
          variant="caption"
          color="text.secondary"
          sx={{display: 'block', mt: 2}}
        >
          Stats reset on cold start (in-memory rolling window).
          Credential material is never stored in Planapus — see
          ADR-0005 (Composio as OAuth broker, supersedes ADR-0001).
          Health probes are live; a refresh re-runs every
          connector's <code>healthCheck()</code>.
        </Typography>
      </Container>
    </Box>
  );
}
